import type {
  CourseDetail,
  CourseIndexOverrides,
  CourseFileIndexEntry,
  CourseSearchRecord,
  DepartmentRecord,
  HomeSummary,
  MedianBoardData,
  ProfessorDetail,
  ProfessorIndexOverrides,
  ProfessorIndexRecord,
  ProfessorLookupEntry,
  SiteSummary,
} from "../../shared/types";
import { termSortValue } from "../../shared/terms";
import { mergeHomeSummary, mergeMedianBoardData, toCourseSearchRecord } from "../../shared/static-artifacts";
import { DATA_BASE_URL } from "./site-config";

let siteSummaryPromise: Promise<SiteSummary> | null = null;
let departmentsPromise: Promise<DepartmentRecord[]> | null = null;
let homeSummaryPromise: Promise<HomeSummary> | null = null;
let courseSearchIndexPromise: Promise<CourseSearchRecord[]> | null = null;
let medianBoardPromise: Promise<MedianBoardData> | null = null;
let professorIndexPromise: Promise<ProfessorIndexRecord[]> | null = null;
let courseIndexOverridesPromise: Promise<CourseIndexOverrides> | null = null;
let professorIndexOverridesPromise: Promise<ProfessorIndexOverrides> | null = null;
const coursePromiseCache = new Map<string, Promise<CourseDetail>>();
const professorPromiseCache = new Map<string, Promise<ProfessorDetail>>();

export class StaticDataNotFoundError extends Error {
  constructor(path: string) {
    super(`Could not find ${path}`);
    this.name = "StaticDataNotFoundError";
  }
}

async function readStaticJson<T>(path: string): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch(path, {
      // Keep responses eligible for conditional caching, but always revalidate so
      // a newly promoted snapshot is visible after a page reload.
      cache: "no-cache",
      signal: controller.signal,
    });
    if (response.status === 404) {
      throw new StaticDataNotFoundError(path);
    }
    if (!response.ok) {
      throw new Error(`Could not load ${path}`);
    }
    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(`Loading ${path} timed out. Please try again.`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function readOptionalStaticJson<T>(path: string, fallback: T, onFailure: () => void): Promise<T> {
  try {
    return await readStaticJson<T>(path);
  } catch {
    onFailure();
    return fallback;
  }
}

export function fetchSiteSummary(): Promise<SiteSummary> {
  siteSummaryPromise ??= readStaticJson<SiteSummary>(`${DATA_BASE_URL}/site-summary.json`).catch((error): never => {
    siteSummaryPromise = null;
    throw error;
  });
  return siteSummaryPromise;
}

export function fetchDepartments(): Promise<DepartmentRecord[]> {
  departmentsPromise ??= readStaticJson<DepartmentRecord[]>(`${DATA_BASE_URL}/departments.json`).catch((error): never => {
    departmentsPromise = null;
    throw error;
  });
  return departmentsPromise;
}

function fetchCourseOverrides(): Promise<CourseIndexOverrides> {
  courseIndexOverridesPromise ??= readOptionalStaticJson(
    `${DATA_BASE_URL}/course-index-overrides.json`,
    {},
    () => {
      courseIndexOverridesPromise = null;
      homeSummaryPromise = null;
      courseSearchIndexPromise = null;
      medianBoardPromise = null;
    },
  );
  return courseIndexOverridesPromise;
}

function sortCourseSearchRecords(courses: CourseSearchRecord[]): CourseSearchRecord[] {
  return courses.sort((left, right) => {
    if (right.legacyReviewCount !== left.legacyReviewCount) {
      return right.legacyReviewCount - left.legacyReviewCount;
    }
    if (left.department !== right.department) {
      return left.department.localeCompare(right.department);
    }
    return left.shortName.localeCompare(right.shortName);
  });
}

export function fetchHomeSummary(): Promise<HomeSummary> {
  homeSummaryPromise ??= Promise.all([
    readStaticJson<HomeSummary>(`${DATA_BASE_URL}/home-summary.json`),
    fetchCourseOverrides(),
    fetchSiteSummary(),
  ]).then(([summary, overrides, siteSummary]) => mergeHomeSummary(summary, overrides, siteSummary.generatedAt)).catch((error) => {
    homeSummaryPromise = null;
    throw error;
  });
  return homeSummaryPromise;
}

export function fetchCourseSearchIndex(): Promise<CourseSearchRecord[]> {
  courseSearchIndexPromise ??= Promise.all([
    readStaticJson<CourseSearchRecord[]>(`${DATA_BASE_URL}/course-search-index.json`),
    fetchCourseOverrides(),
  ]).then(([baseIndex, overrides]) => {
    const merged = new Map(baseIndex.map((course) => [course.courseId, course]));

    for (const [courseId, override] of Object.entries(overrides)) {
      const current = merged.get(courseId);
      merged.set(courseId, current ? { ...current, ...toCourseSearchRecord(override) } : toCourseSearchRecord(override));
    }

    return sortCourseSearchRecords([...merged.values()]);
  }).catch((error) => {
    courseSearchIndexPromise = null;
    throw error;
  });
  return courseSearchIndexPromise;
}

export function fetchCourseIndex(): Promise<CourseSearchRecord[]> {
  return fetchCourseSearchIndex();
}

export function fetchMedianBoard(): Promise<MedianBoardData> {
  medianBoardPromise ??= Promise.all([
    readStaticJson<MedianBoardData>(`${DATA_BASE_URL}/median-board.json`),
    fetchCourseOverrides(),
    fetchSiteSummary(),
  ]).then(([board, overrides, siteSummary]) => mergeMedianBoardData(board, overrides, siteSummary.generatedAt)).catch((error) => {
    medianBoardPromise = null;
    throw error;
  });
  return medianBoardPromise;
}

export function fetchCourse(slug: string): Promise<CourseDetail> {
  const existing = coursePromiseCache.get(slug);
  if (existing) {
    return existing;
  }

  const promise = readStaticJson<CourseDetail>(`${DATA_BASE_URL}/course/${slug}.json`).catch((error) => {
    coursePromiseCache.delete(slug);
    throw error;
  });
  coursePromiseCache.set(slug, promise);
  return promise;
}

export function fetchProfessorIndex(): Promise<ProfessorIndexRecord[]> {
  professorIndexOverridesPromise ??= readOptionalStaticJson(
    `${DATA_BASE_URL}/professors-index-overrides.json`,
    {},
    () => {
      professorIndexOverridesPromise = null;
      professorIndexPromise = null;
    },
  );
  professorIndexPromise ??= Promise.all([
    readStaticJson<ProfessorIndexRecord[]>(`${DATA_BASE_URL}/professors-index.json`),
    professorIndexOverridesPromise,
  ]).then(([baseIndex, overrides]) => {
    const merged = new Map(baseIndex.map((professor) => [professor.slug, professor]));

    for (const [slug, override] of Object.entries(overrides)) {
      const current = merged.get(slug);
      merged.set(slug, current ? { ...current, ...override } : override);
    }

    return [...merged.values()].sort((left, right) => {
      const recentDelta = termSortValue(right.mostRecentTerm) - termSortValue(left.mostRecentTerm);
      if (recentDelta !== 0) {
        return recentDelta;
      }
      if (right.reviewCount !== left.reviewCount) {
        return right.reviewCount - left.reviewCount;
      }
      return left.name.localeCompare(right.name);
    });
  }).catch((error) => {
    professorIndexPromise = null;
    throw error;
  });
  return professorIndexPromise;
}

export function fetchProfessor(slug: string): Promise<ProfessorDetail> {
  const existing = professorPromiseCache.get(slug);
  if (existing) {
    return existing;
  }

  const promise = readStaticJson<ProfessorDetail>(`${DATA_BASE_URL}/professor/${slug}.json`).catch((error) => {
    professorPromiseCache.delete(slug);
    throw error;
  });
  professorPromiseCache.set(slug, promise);
  return promise;
}
